import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { formatDate } from '../../utils/dateFormatter'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { PageBanner } from '../Banners/PageBanner'
import { BlogPost, PopularPost } from '../../types/blog'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'

const BlogDetails = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null)
  const [popularPosts, setPopularPosts] = useState<PopularPost[]>([])
  const [categories, setCategories] = useState<Array<{ name: string, count: number }>>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<boolean>(false)

  const formatContent = (content: string) => {
    if (!content) return ''
    return content.replace(/\n/g, '<br>')
  }

  const requests = useRequests()
  const navigate = useNavigate()
  const { username, isSuperadmin } = useSelector((state: State) => state.user)

  const handleDelete = async () => {
    if (!blogPost) return

    const confirmDelete = window.confirm(`Czy na pewno chcesz usunąć artykuł "${blogPost.title}"? Tej operacji nie można cofnąć.`)
    
    if (!confirmDelete) return

    try {
      setDeleting(true)
      await requests.deleteBlogPost(blogPost.id)
      alert('Artykuł został pomyślnie usunięty.')
      navigate('/blog')
    } catch (err: any) {
      alert(err.response?.data?.message || 'Wystąpił błąd podczas usuwania artykułu.')
      console.error('Błąd podczas usuwania artykułu:', err)
    } finally {
      setDeleting(false)
    }
  }

  const fetchBlogPost = async () => {
    if (!id) return

    try {
      setLoading(true)
      const isNumericId = /^\d+$/.test(id)

      const response = isNumericId
        ? await requests.getBlogPostById(parseInt(id, 10))
        : await requests.getBlogPostBySlug(id)

      const formattedPost = {
        ...response.data,
        date: formatDate(response.data.date)
      }

      setBlogPost(formattedPost)
      setError(null)
    } catch (err) {
      setError('Nie udało się załadować artykułu. Spróbuj ponownie później.')
      console.error('Błąd podczas pobierania artykułu:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchSidebarData = async () => {
    try {
      const [categoriesResponse, popularResponse] = await Promise.all([
        requests.getBlogCategories(),
        requests.getPopularBlogPosts(2)
      ])

      setCategories(categoriesResponse.data)

      const formattedPopular = popularResponse.data.map((post: any) => ({
        ...post,
        date: formatDate(post.date)
      }))

      setPopularPosts(formattedPopular)
    } catch (err) {
      console.error('Błąd podczas pobierania danych dla sidebara:', err)
    }
  }

  useEffect(() => {
    fetchBlogPost()
    fetchSidebarData()
  }, [id, requests])

  if (loading) {
    return (
      <div className='container text-center py-5'>
        <div className='spinner-border text-primary' role='status' style={{ width: '3rem', height: '3rem' }}>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3 text-muted'>Wczytywanie artykułu...</p>
      </div>
    )
  }

  if (error || (blogPost == null)) {
    return (
      <div className='container py-5'>
        <div className='alert alert-danger' role='alert'>
          <i className='fas fa-exclamation-circle me-2' />
          {error || 'Nie znaleziono artykułu.'}
          <p className='mt-2 mb-0'>
            <Link to='/blog' className='alert-link'>Wróć do listy artykułów</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <PageBanner title={blogPost?.title || ''} description={blogPost?.content || ''} />
      <section className='blog-single tw-pt-28 md:tw-pt-32 lg:tw-pt-36 pb-70'>
        <div className='container'>
          {username && isSuperadmin && blogPost && (
            <div className='row mb-4'>
              <div className='col-12'>
                <div className='d-flex justify-content-end'>
                  <Link to={`/blog/edit/${blogPost.id}`} className='btn btn-primary me-2'>
                    <i className='fas fa-edit me-2' /> Edytuj
                  </Link>
                  <button 
                    onClick={handleDelete} 
                    className='btn btn-danger me-2'
                    disabled={deleting}
                  >
                    <i className='fas fa-trash me-2' /> 
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className='row'>
            <div className='col-sm-12 col-md-12 col-lg-8'>
              <div className='post-item mb-0'>
                <div className='post__content'>
                  <div className='post__meta d-flex align-items-center mb-20'>
                    <div className='post__meta-cat'>
                      {blogPost.categories.map((category, index) => (
                        <Link to={`/blog?categories=${category.toLowerCase()}`} key={index}>{category}</Link>
                      ))}
                    </div>
                    <span className='post__meta-date'>{blogPost.date}</span>
                  </div>
                  <h1 className='post__title mb-4'>
                    {blogPost.title}
                  </h1>

                  <div className='post__img mb-4'>
                    <img
                      src={blogPost.imagePresignedUrl || blogPost.imageUrl || `/api/uploads/image/${blogPost.image}`}
                      alt={blogPost.title}
                      className='img-fluid w-100'
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>

                  <div className='post__desc' dangerouslySetInnerHTML={{ __html: formatContent(blogPost.content) }} />
                </div>
              </div>
            </div>

            <div className='col-sm-12 col-md-12 col-lg-4'>
              <aside className='sidebar'>

                <div className='widget widget-categories'>
                  <h5 className='widget__title'>Kategorie</h5>
                  <div className='widget__content'>
                    <ul className='list-unstyled mb-0'>
                      {categories.map((category, index) => (
                        <li key={index}>
                          <Link to={`/blog?categories=${category.name.toLowerCase()}`}>
                            {category.name} <span>({category.count})</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className='widget widget-posts'>
                  <h5 className='widget__title'>Popularne wpisy</h5>
                  <div className='widget__content'>
                    {popularPosts.map((post) => (
                      <div className='widget-post-item d-flex align-items-center' key={post.id}>
                        <div className='widget-post__img'>
                          <Link to={`/blog/${post.slug}`}>
                            <img src={post.imagePresignedUrl || post.image} alt={post.title} />
                          </Link>
                        </div>
                        <div className='widget-post__content'>
                          <span className='widget-post__date'>{post.date}</span>
                          <h4 className='widget-post__title'>
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='widget widget-tags'>
                  <h5 className='widget__title'>Tagi</h5>
                  <div className='widget__content'>
                    <ul className='list-unstyled mb-0'>
                      {Array.from(new Set(blogPost.categories)).map((category, index) => (
                        <li key={index}>
                          <Link to={`/blog?categories=${category.toLowerCase()}`}>{category}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default BlogDetails
